import { useEffect, useMemo, useRef, useState } from "react";
import LayoutPreview from "../../../../Devices/Component/Preview/Custom/LayoutPreview";
import { MediaPlayer } from "../../../../Devices/Component/Preview/Custom/MediaPlayer";
import PlaylistPreview from "../../../../Devices/Component/Preview/Custom/PlaylistPreview";
import AnimatedWrap from "../../../../SharedComponents/DragDrop/animateMedia";
import { useSelector, useDispatch } from "react-redux";
import { setPaused } from "../../../../../redux/Slices/deviceSlice";

// Stable areaId used for the campaign preview player
export const PREVIEW_AREA_ID = "preview-main";

// ---- generic cycler for a layer's items ----
function useContentCycler(contentItems) {
    const [index, setIndex] = useState(0);
    const [switchCount, setSwitchCount] = useState(0);
    const timeoutRef = useRef(null);

    useEffect(() => {
        if (!contentItems || contentItems.length === 0) return;

        const currentItem = contentItems[Math.min(index, contentItems.length - 1)];
        const duration = parseFloat(currentItem?.custom_duration) || 10;

        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            if (contentItems.length > 1) {
                setIndex((prev) => (prev + 1) % contentItems.length);
            }
            // always bump to force a re-key even when only 1 item
            setSwitchCount((c) => c + 1);
        }, duration * 1000);

        return () => clearTimeout(timeoutRef.current);
    }, [index, contentItems]);

    useEffect(() => {
        setIndex(0);
        setSwitchCount(0);
    }, [contentItems]);

    return { item: contentItems?.[index] ?? null, index, switchCount };
}


// ---- helper: pick the right animation config per layer ----
function getLayerAnimConfig(liveSettings, layer) {
    if (!liveSettings) return null;
    switch (layer) {
        case "MAIN":
            return liveSettings.media_animation;
        case "PLAYGROUND":
            return liveSettings.playground_media_animation;
        case "CORNER":
            return liveSettings.corner_media_animation;
        case "BANNER":
            return liveSettings.banner_media_animation;
        default:
            return liveSettings.media_animation;
    }
}

// ---- tiny helper to render an item with optional animation ----
function renderDynamicItem({
    item,
    layer,
    startTime,
    zIndex = 20,
    mainStartTime,
    animConfig, cycleKey = 0, // NEW
}) {
    if (!item) return null;

    const enabled = !!animConfig?.enabled;
    const type = animConfig?.type || "FADE";
    const direction = animConfig?.direction || "CENTER";
    const durationMs = (animConfig?.duration ? animConfig.duration * 1000 : 600) || 600;

    const baseId =
        item.assigned_id || item.media_id || item.playlist_id || item.layout_id || "x";

    const itemKey = `${baseId}-${startTime ?? 0}-${cycleKey}`; // include cycleKey

    const withAnim = (node, className = "w-full h-full") =>
        enabled ? (
            <AnimatedWrap
                itemKey={itemKey}
                type={type}
                direction={direction}
                durationMs={durationMs}
                className={className}
            >
                {node}
            </AnimatedWrap>
        ) : (
            node
        );

    // Layout
    if (item?.layout_id) {
        return withAnim(
            <LayoutPreview layout={item} pause_start_time={startTime} startTime={mainStartTime} />
        );
    }

    // Playlist
    if (item?.playlist_id) {
        return withAnim(<PlaylistPreview
            playlist={item}
            startTime={startTime}
            // pass pause if you have it
            // pause_start_time={pauseStartTimeForThisLayer}
            animConfig={animConfig}
        />);
    }

    // Positioned IMAGE / CORNER
    if (item?.media_type === "IMAGE" || item?.media_type === "CORNER") {
        return (
            <div className="w-full h-full">
                <div
                    className="overflow-hidden absolute cursor-pointer resizable media-preview-corner"
                    style={{
                        left: `${item?.settings?.start_margin ?? 0}%`,
                        top: `${item?.settings?.top_margin ?? 0}%`,
                        width: `${item?.settings?.width ?? 100}%`,
                        height: `${item?.settings?.height ?? 100}%`,
                        zIndex,
                    }}
                >
                    {withAnim(
                        <MediaPlayer
                            data={item}
                            elapsedSeconds={0}
                           customDuration={parseFloat(item?.custom_duration) || 0}
                        />,
                        "absolute inset-0"
                    )}
                </div>
            </div>
        );
    }

    // Default media
    return withAnim(
        <MediaPlayer
            data={item}
            elapsedSeconds={0}
            customDuration={parseFloat(item?.custom_duration) || 0}
        />
    );
}

function ContentPreview({ currentItem, startTime, onEnded }) {
    const [mainStartTime, setMainStartTime] = useState();

    const { campaignDetails } = useSelector((s) => s.global);
    const liveSettings = campaignDetails?.settings?.live || {};

    const playgroundItems = currentItem?.PLAYGROUND || [];
    const cornerItems = currentItem?.CORNER || [];
    const bannerItems = currentItem?.BANNER || [];

    const [layerOneStartTime, setLayerOneStartTime] = useState();
    const [layerTwoStartTime, setLayerTwoStartTime] = useState();
    const [layerThreeStartTime, setLayerThreeStartTime] = useState();

    // fix: correct setters per layer + only set once when toggled on
    useEffect(() => {
        if (currentItem?.show_layer_one) {
            setLayerOneStartTime((t) => (t ?? startTime));
        } else {
            setLayerOneStartTime(undefined);
        }

        if (currentItem?.show_layer_two) {
            setLayerTwoStartTime((t) => (t ?? startTime));
        } else {
            setLayerTwoStartTime(undefined);
        }

        if (currentItem?.show_layer_three) {
            setLayerThreeStartTime((t) => (t ?? startTime));
        } else {
            setLayerThreeStartTime(undefined);
        }
    }, [
        currentItem?.show_layer_one,
        currentItem?.show_layer_two,
        currentItem?.show_layer_three,
        startTime,
    ]);

    useEffect(() => {
        setMainStartTime(Date.now());
    }, [currentItem?.layout_id || currentItem?.media_id || currentItem?.playlist_id]);
    const { item: playgroundItem, switchCount: playgroundTick } = useContentCycler(playgroundItems);
    const { item: cornerItem, switchCount: cornerTick } = useContentCycler(cornerItems);
    const { item: bannerItem, switchCount: bannerTick } = useContentCycler(bannerItems);

    const mainAnim = getLayerAnimConfig(liveSettings, "MAIN");
    const playgroundAnim = getLayerAnimConfig(liveSettings, "PLAYGROUND");
    const cornerAnim = getLayerAnimConfig(liveSettings, "CORNER");
    const bannerAnim = getLayerAnimConfig(liveSettings, "BANNER");

    const memoizedMain = useMemo(() => {
        if (!currentItem) return null;

        const enabled = !!mainAnim?.enabled;
        const type = mainAnim?.type || "FADE";
        const direction = mainAnim?.direction || "CENTER";
        const durationMs =
            (mainAnim?.duration ? mainAnim.duration * 1000 : 600) || 600;

        const key = `${currentItem.assigned_id ||
            currentItem.media_id ||
            currentItem.playlist_id ||
            currentItem.layout_id ||
            "main"
            }-${startTime ?? 0}`;

        const node = currentItem?.playlist_id ? (
            <PlaylistPreview
                key={key}
                playlist={currentItem}
                startTime={startTime}
                animConfig={mainAnim}
            />
        ) : (
            <MediaPlayer
                key={key}
                data={currentItem}
                elapsedSeconds={0}
                customDuration={parseFloat(currentItem?.custom_duration) || 0}
                onEnded={onEnded}
                areaId={PREVIEW_AREA_ID}
            />
        );

        return currentItem?.layout_id ? (
            enabled ? (
                <LayoutPreview
                    layout={currentItem}
                    pause_start_time={startTime}
                    startTime={mainStartTime}
                />
            ) : (
                <LayoutPreview
                    layout={currentItem}
                    pause_start_time={startTime}
                    startTime={startTime}
                />
            )
        ) : enabled ? (
            <AnimatedWrap
                itemKey={key}
                type={type}
                direction={direction}
                durationMs={durationMs}
                className="absolute inset-0 z-10 rounded-lg"
            >
                {node}
            </AnimatedWrap>
        ) : (
            node
        );
    }, [currentItem, startTime, onEnded, mainStartTime, mainAnim?.enabled, mainAnim?.type, mainAnim?.direction, mainAnim?.duration]);

    return (
        <div className="relative w-full h-full text-white font-bold text-xl rounded-lg">
            {/* MAIN */}
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-10 rounded-lg">
                {memoizedMain}
            </div>

            {/* PLAYGROUND (show only if enabled flag is true) */}
            {currentItem?.show_layer_one && (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-20 rounded-lg">
                    {renderDynamicItem({
                        item: playgroundItem,
                        layer: "PLAYGROUND",
                        startTime: layerOneStartTime,
                        zIndex: 20,
                        animConfig: playgroundAnim,
                        cycleKey: playgroundTick,  // NEW
                        mainStartTime: mainStartTime
                    })}
                </div>
            )}


            {/* CORNER */}
            {currentItem?.show_layer_two && (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-30 rounded-lg">
                    {renderDynamicItem({
                        item: cornerItem,
                        layer: "CORNER",
                        startTime: layerTwoStartTime,
                        zIndex: 30,
                        animConfig: cornerAnim,
                        cycleKey: cornerTick,
                    })}
                </div>
            )}

            {/* BANNER */}
            {currentItem?.show_layer_three && (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-40 rounded-lg">
                    {renderDynamicItem({
                        item: bannerItem,
                        layer: "BANNER",
                        startTime: layerThreeStartTime,
                        zIndex: 40,
                        animConfig: bannerAnim,
                        cycleKey: bannerTick,
                    })}
                </div>
            )}
        </div>
    );
}

export default ContentPreview;
